/**
 * Live Message Feed for the Western Express prototype.
 *
 * Folds the WEB-1972 single-stream feed into this prototype as a navigable view
 * alongside Task Feed and Freight Status. Core behavior carries over: one
 * message per row newest-first, truck alias as the primary identifier, a filter
 * bar, a live/paused toggle, recency/urgency sort, and keyboard-driven inline
 * reply. It is fed by the prototype's mocked driver universe (`boards.ts`)
 * rather than the live `messageFeedQuery`, so it runs standalone with demo data.
 *
 * This view adds the net-new Board filter: selecting a board narrows the stream
 * to that board's drivers and surfaces, per board, the count of un-addressed
 * tasks (DispatchTasks in suggested / requires_attention) for its drivers.
 */

import CircleIcon from "@mui/icons-material/Circle";
import LaunchIcon from "@mui/icons-material/Launch";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, formatDistanceToNowStrict } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VList, type VListHandle } from "virtua";

import { type AssignmentOption, assignmentOptionsFor } from "./assignments";
import {
  BOARDS,
  boardForDriver,
  boardUnaddressedCount,
  buildDriverActivity,
  buildDriverThread,
  buildFeedDriverContext,
  buildInitialMessages,
  detailedTaskStates,
  FEED_DRIVER_BY_ID,
  type FeedMessage,
  makeLiveMessage,
  NAMED_BOARD_IDS,
} from "./boards";
import { accent, text, zinc } from "./colors";
import { ActiveContextPanel } from "./components/ActiveContextPanel";
import { TaskAssignmentDialog } from "./components/TaskAssignmentDialog";
import { DriverMessageView } from "./DriverMessageView";
import { compareUrgencyDesc, getUrgencyForItem, type MessageUrgency } from "./messageUrgency";
import type { Board, TaskState } from "./types";

type SortMode = "recency" | "urgency";
type Direction = "driver" | "automated" | "all";

const REFRESH_INTERVAL_MS = 30000;

// Cap the in-memory stream so a long-lived session doesn't grow unbounded as
// live messages and replies are prepended over time.
const MAX_MESSAGES = 2000;

const DIRECTIONS: { value: Direction; label: string }[] = [
  { value: "driver", label: "Driver Messages" },
  { value: "automated", label: "Automated Messages" },
  { value: "all", label: "All Messages" },
];

function isTextEntryElement(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

interface BoardOption {
  board: Board;
  count: number;
}

export function LiveMessageFeedView({
  resolved,
  onToast,
}: {
  resolved: Record<string, TaskState>;
  onToast: (message: string) => void;
}) {
  const [messages, setMessages] = useState<FeedMessage[]>(() => buildInitialMessages(Date.now()));
  const [now, setNow] = useState<number>(() => Date.now());
  const [paused, setPaused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [direction, setDirection] = useState<Direction>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>("recency");
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [unreadOverrides, setUnreadOverrides] = useState<Record<string, "read" | "unread">>({});
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [contextDriverId, setContextDriverId] = useState<string | null>(null);
  const [historyDriverId, setHistoryDriverId] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<{ driverId: string; itemId?: string; label: string } | null>(null);

  const vlistRef = useRef<VListHandle>(null);
  const replyInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const pendingRef = useRef<FeedMessage[]>([]);
  const liveSeqRef = useRef(0);
  const [newSinceCount, setNewSinceCount] = useState(0);

  // Simulated live stream: while not paused, new messages arrive on an interval.
  // While paused, they buffer so we can show a "N new" pill and flush on resume.
  useEffect(() => {
    const id = setInterval(() => {
      const arriveMs = Date.now();
      setNow(arriveMs);
      // Most ticks bring a single message; roughly every third tick brings two.
      const count = arriveMs % 3 === 0 ? 2 : 1;
      const fresh = Array.from({ length: count }, () => makeLiveMessage(arriveMs, ++liveSeqRef.current));
      if (paused) {
        pendingRef.current = [...fresh, ...pendingRef.current];
        setNewSinceCount(pendingRef.current.length);
      } else {
        setMessages((prev) => [...fresh, ...prev].slice(0, MAX_MESSAGES));
      }
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  const resumeLive = useCallback(() => {
    setPaused(false);
    if (pendingRef.current.length > 0) {
      const flush = pendingRef.current;
      pendingRef.current = [];
      setNewSinceCount(0);
      setMessages((prev) => [...flush, ...prev].slice(0, MAX_MESSAGES));
    }
  }, []);

  const togglePaused = useCallback(() => {
    if (paused) resumeLive();
    else setPaused(true);
  }, [paused, resumeLive]);

  // Board membership lookup + per-board un-addressed task counts.
  const boardMemberSets = useMemo(() => new Map(BOARDS.map((b) => [b.id, new Set(b.driverIds)])), []);
  const boardOptions = useMemo<BoardOption[]>(() => {
    const states = detailedTaskStates(resolved);
    // Pin the four ticket-named boards to the top of the picker, then order the
    // rest by descending un-addressed count so the busiest boards surface first.
    return BOARDS.map((board) => ({ board, count: boardUnaddressedCount(board, states) })).sort((a, b) => {
      const aNamed = NAMED_BOARD_IDS.has(a.board.id);
      const bNamed = NAMED_BOARD_IDS.has(b.board.id);
      if (aNamed !== bNamed) return aNamed ? -1 : 1;
      return b.count - a.count;
    });
  }, [resolved]);
  const selectedBoardOptions = useMemo(
    () => boardOptions.filter((o) => selectedBoardIds.includes(o.board.id)),
    [boardOptions, selectedBoardIds],
  );
  const selectedBoardCount = selectedBoardOptions.reduce((sum, o) => sum + o.count, 0);

  const filteredRows = useMemo(() => {
    // Union the membership of every selected board; empty selection = all boards.
    const memberSet = selectedBoardIds.length
      ? new Set(selectedBoardIds.flatMap((id) => [...(boardMemberSets.get(id) ?? [])]))
      : undefined;
    const search = searchValue.trim().toLowerCase();
    const rows = messages.filter((m) => {
      if (memberSet && !memberSet.has(m.driverId)) return false;
      // Automated (system) messages — preplan-failed alerts — show under
      // "Automated Messages" and "All Messages"; "Driver Messages" excludes them.
      const automated = m.emptyCallFailed === true;
      if (direction === "automated" && !automated) return false;
      if (direction === "driver" && (automated || m.direction !== "inbound")) return false;
      if (unreadOnly) {
        const override = unreadOverrides[m.itemId];
        const unread = override === "unread" || (override === undefined && m.unread);
        if (!unread) return false;
      }
      if (search.length > 0) {
        const driver = FEED_DRIVER_BY_ID.get(m.driverId);
        const haystack = `${driver?.truck ?? ""} ${driver?.name ?? ""} ${m.preview}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
    if (sortMode === "urgency") {
      // Tiebreak equal urgency scores by recency so the order is fully defined.
      return [...rows].sort((a, b) => compareUrgencyDesc(a.itemId, b.itemId) || b.createdAt.localeCompare(a.createdAt));
    }
    return rows;
  }, [messages, selectedBoardIds, boardMemberSets, searchValue, direction, unreadOnly, unreadOverrides, sortMode]);

  const selectedIndex = useMemo(
    () => (selectedItemId ? filteredRows.findIndex((r) => r.itemId === selectedItemId) : -1),
    [filteredRows, selectedItemId],
  );
  const selectedRow = selectedIndex >= 0 ? filteredRows[selectedIndex] : undefined;

  useEffect(() => {
    if (selectedItemId == null && filteredRows.length > 0) {
      setSelectedItemId(filteredRows[0].itemId);
    }
  }, [filteredRows, selectedItemId]);

  useEffect(() => {
    if (selectedIndex >= 0) vlistRef.current?.scrollToIndex(selectedIndex, { align: "nearest" });
  }, [selectedIndex]);

  const moveSelection = useCallback(
    (delta: 1 | -1) => {
      if (filteredRows.length === 0) return;
      const current = selectedIndex < 0 ? 0 : selectedIndex;
      const next = Math.max(0, Math.min(filteredRows.length - 1, current + delta));
      setSelectedItemId(filteredRows[next].itemId);
    },
    [filteredRows, selectedIndex],
  );

  const changeSortMode = useCallback((next: SortMode) => {
    setSortMode(next);
    setSelectedItemId(null);
    vlistRef.current?.scrollToIndex(0, { align: "start" });
  }, []);

  const focusReply = useCallback(() => {
    if (selectedIndex < 0) return;
    vlistRef.current?.scrollToIndex(selectedIndex, { align: "nearest" });
    requestAnimationFrame(() => requestAnimationFrame(() => replyInputRef.current?.focus()));
  }, [selectedIndex]);

  const toggleRead = useCallback((row: FeedMessage | undefined) => {
    if (!row) return;
    setUnreadOverrides((prev) => {
      const override = prev[row.itemId];
      const currentlyUnread = override === "unread" || (override === undefined && row.unread);
      return { ...prev, [row.itemId]: currentlyUnread ? "read" : "unread" };
    });
  }, []);

  const sendReply = useCallback(
    (row: FeedMessage | undefined) => {
      if (!row) return;
      const driver = FEED_DRIVER_BY_ID.get(row.driverId);
      const draft = (replyDrafts[row.itemId] ?? "").trim();
      if (draft.length === 0) return;
      const reply: FeedMessage = {
        itemId: `reply_${row.itemId}_${Date.now()}`,
        driverId: row.driverId,
        createdAt: new Date().toISOString(),
        preview: draft,
        direction: "outbound",
        unread: false,
        isSilent: false,
        isFromBot: false,
        hasTranscription: false,
      };
      setMessages((prev) => [reply, ...prev].slice(0, MAX_MESSAGES));
      setReplyDrafts((prev) => {
        const next = { ...prev };
        delete next[row.itemId];
        return next;
      });
      onToast(`Reply sent to ${driver?.name ?? "driver"} · Truck ${driver?.truck ?? "—"}`);
      replyInputRef.current?.blur();
      moveSelection(1);
    },
    [replyDrafts, moveSelection, onToast],
  );

  const searchSelectedDriver = useCallback((row: FeedMessage | undefined) => {
    const driver = row ? FEED_DRIVER_BY_ID.get(row.driverId) : undefined;
    if (driver) setSearchValue(driver.truck);
  }, []);

  // Right-side overlays — Active Context (`C`) and Message History (`O`) — both
  // toggle per driver and are mutually exclusive so only one pops over at a time.
  const toggleContext = useCallback((driverId: string) => {
    setHistoryDriverId(null);
    setContextDriverId((current) => (current === driverId ? null : driverId));
  }, []);
  const closeContext = useCallback(() => setContextDriverId(null), []);
  const toggleContextForSelected = useCallback(() => {
    if (selectedRow) toggleContext(selectedRow.driverId);
  }, [selectedRow, toggleContext]);
  const contextDriver = useMemo(
    () => (contextDriverId ? buildFeedDriverContext(contextDriverId) : null),
    [contextDriverId],
  );

  const toggleHistory = useCallback((driverId: string) => {
    setContextDriverId(null);
    setHistoryDriverId((current) => (current === driverId ? null : driverId));
  }, []);
  const closeHistory = useCallback(() => setHistoryDriverId(null), []);
  const toggleHistoryForSelected = useCallback(() => {
    if (selectedRow) toggleHistory(selectedRow.driverId);
  }, [selectedRow, toggleHistory]);
  const historyDriver = useMemo(
    () => (historyDriverId ? buildFeedDriverContext(historyDriverId) : null),
    [historyDriverId],
  );
  const historyMessages = useMemo(() => (historyDriverId ? buildDriverThread(historyDriverId) : []), [historyDriverId]);
  const historyActivity = useMemo(
    () => (historyDriverId ? buildDriverActivity(historyDriverId) : []),
    [historyDriverId],
  );

  // Task assignment: hand a message off to another manager / coverage group.
  const openAssignFor = useCallback((row: FeedMessage | undefined) => {
    if (!row) return;
    const driver = FEED_DRIVER_BY_ID.get(row.driverId);
    setAssignTarget({
      driverId: row.driverId,
      itemId: row.itemId,
      label: `Truck ${driver?.truck ?? "—"} · ${driver?.name ?? "driver"}`,
    });
  }, []);
  const submitAssign = useCallback(
    (option: AssignmentOption) => {
      if (assignTarget?.itemId) {
        // Handing the message off clears it from the dispatcher's queue → mark read.
        setUnreadOverrides((prev) => ({ ...prev, [assignTarget.itemId as string]: "read" }));
      }
      onToast(`Assigned to ${option.name}`);
      setAssignTarget(null);
    },
    [assignTarget, onToast],
  );

  // Keyboard shortcuts mirror WEB-1972: Tab/arrows navigate, R reply, Enter
  // send + advance, M toggle read, O open channel, S search-this-driver, U sort.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const inInput = isTextEntryElement(e.target);
      if (e.key === "Tab") {
        e.preventDefault();
        if (inInput && e.target instanceof HTMLElement) e.target.blur();
        moveSelection(e.shiftKey ? -1 : 1);
        return;
      }
      if (inInput) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          sendReply(selectedRow);
        } else if (e.key === "Escape" && e.target instanceof HTMLElement) {
          e.preventDefault();
          e.target.blur();
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape" && (contextDriverId || historyDriverId)) {
        e.preventDefault();
        closeContext();
        closeHistory();
        return;
      }
      const key = e.key.toLowerCase();
      if (key === "r") {
        e.preventDefault();
        focusReply();
      } else if (key === "a") {
        e.preventDefault();
        openAssignFor(selectedRow);
      } else if (key === "c") {
        e.preventDefault();
        toggleContextForSelected();
      } else if (key === "m") {
        e.preventDefault();
        toggleRead(selectedRow);
      } else if (key === "o") {
        e.preventDefault();
        toggleHistoryForSelected();
      } else if (key === "s") {
        e.preventDefault();
        searchSelectedDriver(selectedRow);
      } else if (key === "u") {
        e.preventDefault();
        changeSortMode(sortMode === "recency" ? "urgency" : "recency");
      } else if (e.key === "Enter") {
        e.preventDefault();
        focusReply();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        moveSelection(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        moveSelection(-1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    moveSelection,
    sendReply,
    selectedRow,
    focusReply,
    toggleRead,
    searchSelectedDriver,
    changeSortMode,
    sortMode,
    toggleContextForSelected,
    closeContext,
    contextDriverId,
    toggleHistoryForSelected,
    closeHistory,
    historyDriverId,
    openAssignFor,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, color: text.primary }}>
      <Header
        paused={paused}
        onTogglePaused={togglePaused}
        newSinceCount={newSinceCount}
        onResume={resumeLive}
        visibleCount={filteredRows.length}
        sortMode={sortMode}
        boardNames={selectedBoardOptions.map((o) => o.board.name)}
        boardCount={selectedBoardCount}
      />
      <FilterBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        direction={direction}
        onDirectionChange={setDirection}
        unreadOnly={unreadOnly}
        onUnreadOnlyChange={setUnreadOnly}
        sortMode={sortMode}
        onSortModeChange={changeSortMode}
        boardOptions={boardOptions}
        selectedBoards={selectedBoardOptions}
        onBoardChange={(opts) => {
          setSelectedBoardIds(opts.map((o) => o.board.id));
          setSelectedItemId(null);
        }}
      />
      <KeyboardLegend />
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        {filteredRows.length === 0 ? (
          <EmptyState boardsSelected={selectedBoardOptions.length > 0} />
        ) : (
          <VList ref={vlistRef} style={{ height: "100%" }}>
            {filteredRows.map((row) => {
              const override = unreadOverrides[row.itemId];
              const unread = override === "unread" || (override === undefined && row.unread);
              return (
                <MessageRow
                  key={row.itemId}
                  row={row}
                  now={now}
                  unread={unread}
                  urgency={getUrgencyForItem(row.itemId)}
                  showUrgency={sortMode === "urgency"}
                  selected={row.itemId === selectedItemId}
                  replyText={replyDrafts[row.itemId] ?? ""}
                  onReplyChange={(v) => setReplyDrafts((prev) => ({ ...prev, [row.itemId]: v }))}
                  onSendReply={() => sendReply(row)}
                  onToggleRead={() => toggleRead(row)}
                  onOpenHistory={() => toggleHistory(row.driverId)}
                  historyOpen={row.driverId === historyDriverId}
                  onOpenContext={() => toggleContext(row.driverId)}
                  contextOpen={row.driverId === contextDriverId}
                  onOpenAssign={() => openAssignFor(row)}
                  onClick={() => setSelectedItemId(row.itemId)}
                  inputRef={replyInputRef}
                />
              );
            })}
          </VList>
        )}
        {contextDriver && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 5, display: "flex", justifyContent: "flex-end" }}>
            <Box
              onClick={closeContext}
              sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.5)", cursor: "pointer" }}
            />
            <Box
              sx={{
                position: "relative",
                height: "100%",
                width: { xs: "100%", md: "min(840px, 86%)" },
                bgcolor: zinc[950],
                borderLeft: `1px solid ${zinc[800]}`,
                boxShadow: "-16px 0 40px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                animation: "contextSlideIn 160ms ease",
                "@keyframes contextSlideIn": {
                  from: { transform: "translateX(28px)", opacity: 0.5 },
                  to: { transform: "translateX(0)", opacity: 1 },
                },
              }}
            >
              <ActiveContextPanel
                driver={contextDriver}
                onOpenMessageHistory={() => toggleHistory(contextDriver.id)}
                onAssign={() =>
                  setAssignTarget({
                    driverId: contextDriver.id,
                    label: `Truck ${contextDriver.truck} · ${contextDriver.name}`,
                  })
                }
                onClose={closeContext}
              />
            </Box>
          </Box>
        )}
        {historyDriver && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 5, display: "flex", justifyContent: "flex-end" }}>
            <Box
              onClick={closeHistory}
              sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.5)", cursor: "pointer" }}
            />
            <Box
              sx={{
                position: "relative",
                height: "100%",
                width: { xs: "100%", md: "66.6667%" },
                bgcolor: zinc[950],
                borderLeft: `1px solid ${zinc[800]}`,
                boxShadow: "-16px 0 40px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                animation: "contextSlideIn 160ms ease",
                "@keyframes contextSlideIn": {
                  from: { transform: "translateX(28px)", opacity: 0.5 },
                  to: { transform: "translateX(0)", opacity: 1 },
                },
              }}
            >
              <DriverMessageView
                key={historyDriverId}
                driverName={historyDriver.name}
                truck={historyDriver.truck}
                messages={historyMessages}
                activity={historyActivity}
                onBack={closeHistory}
              />
            </Box>
          </Box>
        )}
      </Box>
      {assignTarget && (
        <TaskAssignmentDialog
          title={`Assign ${assignTarget.label}`}
          options={assignmentOptionsFor(assignTarget.driverId)}
          onSubmit={submitAssign}
          onCancel={() => setAssignTarget(null)}
        />
      )}
    </Box>
  );
}

function Header({
  paused,
  onTogglePaused,
  newSinceCount,
  onResume,
  visibleCount,
  sortMode,
  boardNames,
  boardCount,
}: {
  paused: boolean;
  onTogglePaused: () => void;
  newSinceCount: number;
  onResume: () => void;
  visibleCount: number;
  sortMode: SortMode;
  boardNames: string[];
  boardCount: number;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        pt: 2,
        pb: 1,
        borderBottom: `1px solid ${zinc[800]}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Live Message Feed</Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: paused ? zinc[800] : accent.greenSoft,
            color: paused ? text.muted : accent.green,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          <CircleIcon
            sx={{
              fontSize: 8,
              animation: paused ? "none" : "feedPulse 1.6s ease-in-out infinite",
              "@keyframes feedPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
            }}
          />
          {paused ? "Paused" : "Live"}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: text.secondary }}>
        {visibleCount.toLocaleString()} messages · sorted by{" "}
        <Box component="span" sx={{ color: sortMode === "urgency" ? accent.amber : text.primary, fontWeight: 700 }}>
          {sortMode}
        </Box>
        {boardNames.length > 0 && (
          <>
            {boardNames.length === 1 ? " · board " : " · boards "}
            <Box component="span" sx={{ color: accent.purple, fontWeight: 700 }}>
              {boardNames.join(", ")}
            </Box>{" "}
            <Box component="span" sx={{ color: boardCount > 0 ? accent.red : accent.green }}>
              ({boardCount} un-addressed)
            </Box>
          </>
        )}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title={paused ? "Resume live updates" : "Pause to read steady"}>
        <IconButton onClick={onTogglePaused} sx={{ color: paused ? accent.green : text.secondary }} size="small">
          {paused ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
        </IconButton>
      </Tooltip>
      {paused && newSinceCount > 0 && (
        <Chip
          label={`${newSinceCount} new`}
          size="small"
          onClick={onResume}
          sx={{ bgcolor: accent.blue, color: "white", fontWeight: 700, cursor: "pointer" }}
        />
      )}
    </Box>
  );
}

function FilterBar({
  searchValue,
  onSearchChange,
  direction,
  onDirectionChange,
  unreadOnly,
  onUnreadOnlyChange,
  sortMode,
  onSortModeChange,
  boardOptions,
  selectedBoards,
  onBoardChange,
}: {
  searchValue: string;
  onSearchChange: (v: string) => void;
  direction: Direction;
  onDirectionChange: (v: Direction) => void;
  unreadOnly: boolean;
  onUnreadOnlyChange: (v: boolean) => void;
  sortMode: SortMode;
  onSortModeChange: (mode: SortMode) => void;
  boardOptions: BoardOption[];
  selectedBoards: BoardOption[];
  onBoardChange: (opts: BoardOption[]) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${zinc[800]}`,
        flexWrap: "wrap",
      }}
    >
      <TextField
        size="small"
        placeholder="Search by truck #, driver, or message…"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: text.muted, fontSize: 20 }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ minWidth: 280, flexGrow: 1, maxWidth: 420, ...fieldSx }}
      />
      <Autocomplete<BoardOption, true>
        multiple
        disableCloseOnSelect
        limitTags={2}
        size="small"
        options={boardOptions}
        value={selectedBoards}
        onChange={(_, value) => onBoardChange(value)}
        getOptionLabel={(o) => o.board.name}
        isOptionEqualToValue={(a, b) => a.board.id === b.board.id}
        sx={{ minWidth: 240, maxWidth: 360 }}
        renderInput={(params) => (
          <TextField {...params} placeholder={selectedBoards.length > 0 ? "" : "All boards"} sx={fieldSx} />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{option.board.name}</Typography>
            <Chip
              label={`${option.count}`}
              size="small"
              sx={{
                height: 18,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: option.count > 0 ? accent.redSoft : zinc[800],
                color: option.count > 0 ? accent.red : text.muted,
              }}
            />
          </Box>
        )}
      />
      <Select
        size="small"
        value={direction}
        onChange={(e) => onDirectionChange(e.target.value as Direction)}
        sx={selectSx}
        MenuProps={menuPropsSx}
      >
        {DIRECTIONS.map((d) => (
          <MenuItem key={d.value} value={d.value}>
            {d.label}
          </MenuItem>
        ))}
      </Select>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: text.secondary }}>
        <Switch size="small" checked={unreadOnly} onChange={(e) => onUnreadOnlyChange(e.target.checked)} />
        <Typography sx={{ fontSize: 13 }}>Unread only</Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <ToggleButtonGroup
        value={sortMode}
        exclusive
        size="small"
        onChange={(_, value) => value && onSortModeChange(value as SortMode)}
        sx={{
          bgcolor: zinc[800],
          "& .MuiToggleButton-root": {
            color: text.secondary,
            borderColor: zinc[700],
            textTransform: "none",
            fontSize: 13,
            px: 1.25,
            py: 0.5,
            gap: 0.5,
            "&.Mui-selected": { bgcolor: zinc[700], color: text.primary, "&:hover": { bgcolor: zinc[600] } },
          },
        }}
      >
        <ToggleButton value="recency">
          <ScheduleIcon sx={{ fontSize: 16 }} />
          Recency
        </ToggleButton>
        <ToggleButton value="urgency">
          <LocalFireDepartmentIcon sx={{ fontSize: 16, color: sortMode === "urgency" ? accent.amber : "inherit" }} />
          Urgency
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

function KeyboardLegend() {
  const items: { keys: string[]; label: string }[] = [
    { keys: ["Tab"], label: "Move rows" },
    { keys: ["R"], label: "Reply" },
    { keys: ["Enter"], label: "Send + advance" },
    { keys: ["A"], label: "Assign" },
    { keys: ["C"], label: "Active context" },
    { keys: ["M"], label: "Read / unread" },
    { keys: ["O"], label: "Message history" },
    { keys: ["S"], label: "Search driver" },
    { keys: ["U"], label: "Recency / Urgency" },
  ];
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        alignItems: "center",
        px: 2.5,
        py: 0.75,
        borderBottom: `1px solid ${zinc[800]}`,
        bgcolor: "rgba(0,0,0,0.25)",
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {item.keys.map((k) => (
            <Box
              key={k}
              component="kbd"
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 11,
                fontWeight: 700,
                color: text.secondary,
                bgcolor: zinc[800],
                border: `1px solid ${zinc[700]}`,
                borderRadius: 0.5,
                px: 0.6,
                py: 0.1,
              }}
            >
              {k}
            </Box>
          ))}
          <Typography sx={{ fontSize: 11, color: text.muted, ml: 0.25 }}>{item.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

const GRID_COLUMNS_BASE = "104px 96px 200px 110px 1fr 132px 80px";
const GRID_COLUMNS_URGENCY = "104px 60px 96px 200px 110px 1fr 132px 80px";

function MessageRow({
  row,
  now,
  unread,
  selected,
  replyText,
  urgency,
  showUrgency,
  onReplyChange,
  onSendReply,
  onToggleRead,
  onOpenHistory,
  historyOpen,
  onOpenContext,
  contextOpen,
  onOpenAssign,
  onClick,
  inputRef,
}: {
  row: FeedMessage;
  now: number;
  unread: boolean;
  selected: boolean;
  replyText: string;
  urgency: MessageUrgency;
  showUrgency: boolean;
  onReplyChange: (value: string) => void;
  onSendReply: () => void;
  onToggleRead: () => void;
  onOpenHistory: () => void;
  historyOpen: boolean;
  onOpenContext: () => void;
  contextOpen: boolean;
  onOpenAssign: () => void;
  onClick: () => void;
  inputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}) {
  const driver = FEED_DRIVER_BY_ID.get(row.driverId);
  const driverBoard = boardForDriver(row.driverId);
  const createdMs = new Date(row.createdAt).getTime();
  const ageSeconds = Math.max(0, Math.floor((now - createdMs) / 1000));
  const isFresh = ageSeconds < 60;
  const relative =
    ageSeconds < 60 ? `${ageSeconds}s ago` : formatDistanceToNowStrict(new Date(row.createdAt), { addSuffix: true });
  const inbound = row.direction === "inbound";

  const setReplyInput = useCallback(
    (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      if (selected) inputRef.current = el;
    },
    [selected, inputRef],
  );

  const leftBorder = selected
    ? `3px solid ${accent.blue}`
    : showUrgency
      ? `3px solid ${urgency.color}`
      : unread
        ? `3px solid ${accent.blue}`
        : "3px solid transparent";

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        bgcolor: selected ? "rgba(59, 130, 246, 0.32)" : unread ? "rgba(59,130,246,0.06)" : "transparent",
        borderLeft: leftBorder,
        borderBottom: `1px solid ${zinc[800]}`,
        "&:hover": { bgcolor: selected ? "rgba(59, 130, 246, 0.38)" : zinc[800] },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: showUrgency ? GRID_COLUMNS_URGENCY : GRID_COLUMNS_BASE,
          gap: 2,
          px: 2.5,
          py: 1.25,
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              fontWeight: 700,
              color: isFresh ? accent.green : text.secondary,
            }}
          >
            {format(new Date(row.createdAt), "HH:mm:ss")}
          </Typography>
          <Typography sx={{ fontSize: 11, color: text.muted }}>{relative}</Typography>
        </Box>

        {showUrgency && (
          <Tooltip title={`Urgency ${urgency.score}/99 · ${urgency.label} · ${urgency.summary}`}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                justifySelf: "start",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12,
                fontWeight: 700,
                color: urgency.color,
                border: `1px solid ${urgency.color}66`,
                borderRadius: 0.75,
                px: 0.6,
                py: 0.15,
              }}
            >
              {urgency.level === "critical" && <LocalFireDepartmentIcon sx={{ fontSize: 13 }} />}
              {urgency.score}
            </Box>
          </Tooltip>
        )}

        <Tooltip title="Truck # (demo — fabricated from driver id)">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {unread && <CircleIcon sx={{ fontSize: 9, color: accent.red }} />}
            <Typography
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 15,
                fontWeight: 800,
                color: text.primary,
                letterSpacing: "0.02em",
              }}
            >
              {driver?.truck ?? "—"}
            </Typography>
          </Box>
        </Tooltip>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13.5,
              color: text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {driver?.name ?? "Unknown driver"}
          </Typography>
          {driver?.drivingStatus && (
            <Typography sx={{ fontSize: 11, color: text.muted }}>{driver.drivingStatus.replace("_", " ")}</Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {driverBoard ? (
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: accent.purple,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {driverBoard.name}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: zinc[700] }}>—</Typography>
          )}
        </Box>

        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: row.emptyCallFailed ? 700 : 400,
            color: row.emptyCallFailed ? accent.amber : row.isSilent ? accent.red : text.secondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.preview}
        </Typography>

        <Box sx={{ minWidth: 0 }}>
          {row.assignedToMe && row.assignedBy ? (
            <Tooltip title={`Assigned to you by ${row.assignedBy}`}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 14, color: accent.purple, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: 12,
                    color: accent.purple,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.assignedBy}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            <Typography sx={{ fontSize: 12, color: zinc[700] }}>—</Typography>
          )}
        </Box>

        <Box sx={{ justifySelf: "end" }}>
          <Chip
            label={row.emptyCallFailed ? "FAILED" : inbound ? "IN" : "OUT"}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: row.emptyCallFailed ? accent.amberSoft : inbound ? accent.greenSoft : accent.blueSoft,
              color: row.emptyCallFailed ? accent.amber : inbound ? accent.green : accent.blue,
            }}
          />
        </Box>
      </Box>

      {selected && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, pb: 1.25, ml: "104px" }}
        >
          <ReplyOutlinedIcon sx={{ fontSize: 16, color: text.muted }} />
          <TextField
            inputRef={setReplyInput}
            size="small"
            fullWidth
            placeholder={`Reply to Truck ${driver?.truck ?? "—"}…  (R to focus · Enter to send)`}
            value={replyText}
            onChange={(e) => onReplyChange(e.target.value)}
            sx={fieldSx}
          />
          <Tooltip title="Send (Enter)">
            <span>
              <IconButton
                onClick={onSendReply}
                disabled={replyText.trim().length === 0}
                size="small"
                sx={{ color: accent.blue, "&.Mui-disabled": { color: zinc[600] } }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={unread ? "Mark read (M)" : "Mark unread (M)"}>
            <IconButton onClick={onToggleRead} size="small" sx={{ color: text.muted }}>
              <CircleIcon sx={{ fontSize: 16, color: unread ? accent.red : zinc[600] }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Assign (A)">
            <IconButton onClick={onOpenAssign} size="small" sx={{ color: text.muted }}>
              <PersonAddAltOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={contextOpen ? "Close active context (C)" : "Open active context (C)"}>
            <IconButton onClick={onOpenContext} size="small" sx={{ color: contextOpen ? accent.blue : text.muted }}>
              <ViewSidebarOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={historyOpen ? "Close message history (O)" : "Open message history (O)"}>
            <IconButton onClick={onOpenHistory} size="small" sx={{ color: historyOpen ? accent.blue : text.muted }}>
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

function EmptyState({ boardsSelected }: { boardsSelected: boolean }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        color: text.muted,
      }}
    >
      <Typography sx={{ fontSize: 15 }}>
        {boardsSelected ? "No messages from the selected boards in this window" : "No messages in this window"}
      </Typography>
      <Typography sx={{ fontSize: 13 }}>Try widening the time range or clearing filters.</Typography>
    </Box>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: zinc[800],
    color: text.primary,
    fontSize: 13.5,
    "& fieldset": { borderColor: zinc[700] },
    "&:hover fieldset": { borderColor: zinc[600] },
    "&.Mui-focused fieldset": { borderColor: accent.blue },
  },
  "& .MuiInputBase-input::placeholder": { color: text.muted, opacity: 1 },
} as const;

const selectSx = {
  minWidth: 150,
  bgcolor: zinc[800],
  color: text.primary,
  fontSize: 13.5,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: zinc[700] },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: zinc[600] },
  "& .MuiSelect-icon": { color: text.muted },
} as const;

const menuPropsSx = {
  PaperProps: {
    sx: { bgcolor: zinc[800], color: text.primary, "& .MuiMenuItem-root:hover": { bgcolor: zinc[700] } },
  },
} as const;
